<script lang="ts" setup>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { GpAlert, GpBrandLoading, GpBrandMessage, GpCard } from '@gportal/uikit';
import { type ServiceError } from 'gpcore-apikit/grpc/types';
import { useFlavourStore } from 'gpcore-apikit/store/pinia/flavour';
import { useNodeStore } from 'gpcore-apikit/store/pinia/node';
import { BillingPeriod } from 'gpcore-grpc/gpcore/api/cloud/v1/billing_pb';
import { type Flavour } from 'gpcore-grpc/gpcore/api/cloud/v1/flavour_pb';
import { type Node } from 'gpcore-grpc/gpcore/api/cloud/v1/node_pb';
import AsyncModal from 'gpcore-uikit/components/new/AsyncModal.vue';
import { computed, ref, toRef, watch, watchEffect } from 'vue';
import { convertToDate } from 'gpcore-apikit/lib/date';

const props = withDefaults(defineProps<{ show: boolean; node: Node }>(), { show: false });

const emit = defineEmits<{ (e: 'close') }>();

const newBillingPeriod = ref<BillingPeriod>(BillingPeriod.UNSPECIFIED);

const propsNode = toRef(props, 'node');
watchEffect(() => {
  if (
    propsNode.value.targetBillingPeriod !== undefined &&
    propsNode.value.targetBillingPeriod !== BillingPeriod.UNSPECIFIED
  ) {
    newBillingPeriod.value = propsNode.value.targetBillingPeriod;
  } else {
    newBillingPeriod.value = propsNode.value.billingPeriod;
  }
});

const paidUntilDate = computed(() =>
  propsNode.value != null ? convertToDate(propsNode.value.paidUntilAt) : undefined,
);

const paidUntilFormatted = computed(() =>
  propsNode.value ? paidUntilDate.value.toUTCString() : undefined,
);

const nodeStore = useNodeStore();
const flavourStore = useFlavourStore();
const nodeFlavour = ref<Flavour>();

const save = () =>
  nodeStore.changeNodeBillingPeriod({
    node: props.node,
    billingPeriod: newBillingPeriod.value,
  });
const onDone = () => {
  emit('close');
};

const isLoading = ref(false);
const loadingError = ref('');
const reloadBillingPeriods = () => {
  isLoading.value = true;

  const datacenterId = props.node.datacenter.id;
  const { projectId } = props.node;
  flavourStore
    .reloadFlavoursByIDs(datacenterId, projectId, true)
    .then(() => {
      nodeFlavour.value = flavourStore.flavours.find(
        (flavour) => flavour.id === nodeStore.node.flavour.id,
      );
      loadingError.value = '';
    })
    .catch((error) => {
      loadingError.value = (error as ServiceError).message;
    })
    .finally(() => {
      isLoading.value = false;
    });
};

watch(
  () => props.show,
  () => {
    if (props.show) {
      reloadBillingPeriods();
    }
  },
);
</script>

<template>
  <AsyncModal
    :async-function="save"
    :header-icon="['fas', 'fa-wrench']"
    :is-valid="true"
    :show="show"
    confirm-text="Save"
    header-text="Change billing period"
    @close="emit('close')"
    @done="onDone"
  >
    <template #content>
      <GpBrandLoading
        v-if="isLoading"
        class="tw-my-4"
        loading
        message="Loading billing options ..."
      />
      <GpBrandMessage v-else-if="loadingError.length" class="tw-my-4">
        <pre>{{ loadingError }}</pre>

        <a class="tw-mt-2 tw-cursor-pointer" type="button" @click="reloadBillingPeriods">
          <FontAwesomeIcon :icon="['fas', 'fa-undo']" class="tw-mr-2" />
          Try again
        </a>
      </GpBrandMessage>
      <div v-else-if="nodeFlavour" class="tw-px-4 tw-py-8">
        <p class="tw-mb-4">Currently, your node is billed per hour.</p>
        <p class="tw-mb-4">
          You can switch to a monthly billed period for this node, if selected this will start
          immediately and can't be undone.
        </p>
        <GpCard
          v-if="propsNode.billingPeriod === BillingPeriod.HOURLY"
          :active="newBillingPeriod === BillingPeriod.HOURLY"
          :class="{
            'tw-cursor-pointer': newBillingPeriod !== BillingPeriod.HOURLY,
          }"
          body-class="tw-p-4 tw-bg-gp-midnight-2"
          class="tw-mt-4"
          @click="newBillingPeriod = BillingPeriod.HOURLY"
        >
          <template #content>
            <div class="tw-flex tw-items-center">
              <FontAwesomeIcon class="tw-mr-4 tw-text-xl" icon="clock" />
              <p class="tw-flex-1">Hourly</p>
              <!--<div class="tw-flex-1">
                <h6 class="tw-mb-2">Hourly</h6>
                <span v-if="nodeFlavour.pricePerHour != null" class="tw-font-bold">
                  {{ nodeFlavour.pricePerHour.formatted }} / hour
                </span>
              </div>-->
            </div>
          </template>
        </GpCard>
        <GpCard
          v-if="nodeFlavour.pricePerMonth !== undefined"
          :active="newBillingPeriod === BillingPeriod.MONTHLY"
          :class="{
            'tw-cursor-pointer': newBillingPeriod !== BillingPeriod.MONTHLY,
          }"
          body-class="tw-p-4 tw-bg-gp-midnight-2"
          class="tw-mt-4"
          @click="newBillingPeriod = BillingPeriod.MONTHLY"
        >
          <template #content>
            <div class="tw-flex tw-items-center">
              <FontAwesomeIcon class="tw-mr-4 tw-text-xl" icon="calendar" />
              <p class="tw-flex-1">Monthly</p>

              <!--<div class="tw-flex-1">
                <h6 class="tw-mb-2">Monthly</h6>
                <span v-if="nodeFlavour.pricePerMonth != null" class="tw-font-bold">
                  {{ nodeFlavour.pricePerMonth.formatted }} / month
                </span>
              </div>-->
            </div>
          </template>
        </GpCard>
        <GpCard
          v-if="node.billingPeriod !== BillingPeriod.HOURLY"
          :active="newBillingPeriod === BillingPeriod.DELETED"
          :class="{
            'tw-cursor-pointer': newBillingPeriod !== BillingPeriod.HOURLY,
          }"
          body-class="tw-p-4 tw-bg-gp-midnight-2"
          class="tw-mt-4"
          @click="newBillingPeriod = BillingPeriod.DELETED"
        >
          <template #content>
            <div class="tw-flex tw-items-center">
              <FontAwesomeIcon class="tw-mr-4 tw-text-xl" icon="trash" />
              <p class="tw-flex-1">Delete on next billing interval.</p>
            </div>
          </template>
        </GpCard>

        <GpAlert
          v-if="propsNode.billingPeriod === BillingPeriod.MONTHLY"
          alert-type="warning"
          class="tw-mt-4"
          role="alert"
          size="sm"
        >
          <strong>Billed monthly</strong>
          The node you're going to change the billing period of is currently billed monthly. Any
          change will take effect after
          <strong> {{ paidUntilFormatted }} </strong>.
        </GpAlert>
      </div>
    </template>
  </AsyncModal>
</template>
